import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import { ComposicaoExcluirApiRequest, ComposicaoSalvarApiRequest, TipoComposicao } from '@/core/api/sicom-api/registro/orgao/composicao.model';
import { OrgaoService } from '@/core/api/sicom-api/registro/orgao/orgao.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CustomService } from '@/shared/services/custom.service';
import { environment } from '@env/environment';
import { Page } from '@/shared/models/page.model';

export interface CargoOpcao {
    id: number;
    descricao: string;
}

interface CargoApiResponse {
    id: number;
    dsCargo: string;
}

interface PessoaLocalizadaApiResponse {
    id?: number;
    uf?: string;
    nome?: string;
    crm?: string;
}

export interface MedicoComposicaoLookup {
    idMedico: number;
    ufMedico: string;
    nome: string;
}

export interface ComposicaoLinha {
    idMedico: number;
    ufMedico: string;
    uf: string;
    nome: string;
    cargo: string;
    dataEntrada: string;
    dataSaida: string;
    dataEntradaIso: string | null;
    dataSaidaIso: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class ComposicaoService extends CustomService {
    private readonly cargoApiUrl = environment.apis.apiSicom + '/api/v1/sistema/registro/cargo';
    private readonly pessoaLocalizarApiUrl = environment.apis.apiSicom + '/api/v1/cadastro/pessoa/localizar';

    constructor(
        private orgaoService: OrgaoService,
        private http: HttpClient
    ) {
        super();
    }

    buscarPorUf(uf: string, tipo: TipoComposicao): Observable<ComposicaoLinha[]> {
        return this.orgaoService.buscarComposicaoPorUf(uf, tipo).pipe(
            map((response) => this.mapearLinhas(response)),
            catchError(this.handleError)
        );
    }

    buscarPorPessoa(pessoa: Pessoa, tipo: TipoComposicao): Observable<ComposicaoLinha[]> {
        return this.orgaoService.buscarComposicao(Number(pessoa.id), pessoa.uf, tipo).pipe(
            map((response) => this.mapearLinhas(response)),
            catchError(this.handleError)
        );
    }

    salvar(request: ComposicaoSalvarApiRequest): Observable<ComposicaoLinha> {
        return this.orgaoService.salvarComposicao(request).pipe(
            map((response) => this.mapearLinha(response)),
            catchError(this.handleError)
        );
    }

    salvarDelegacia(request: ComposicaoSalvarApiRequest): Observable<ComposicaoLinha> {
        return this.orgaoService.salvarComposicaoDelegacia(request).pipe(
            map((response) => this.mapearLinha(response)),
            catchError(this.handleError)
        );
    }

    excluir(request: ComposicaoExcluirApiRequest): Observable<boolean> {
        return this.orgaoService.excluirComposicao(request).pipe(catchError(this.handleError));
    }

    excluirDelegacia(request: ComposicaoExcluirApiRequest): Observable<boolean> {
        return this.orgaoService.excluirComposicaoDelegacia(request).pipe(catchError(this.handleError));
    }

    listarCargos(): Observable<CargoOpcao[]> {
        return this.http.get<CargoApiResponse[]>(this.cargoApiUrl).pipe(
            map((response) =>
                (response || []).map((cargo) => ({
                    id: Number(cargo.id),
                    descricao: cargo.dsCargo || ''
                }))
            ),
            catchError(this.handleError)
        );
    }

    buscarMedicoPorCrm(crm: string, uf: string): Observable<MedicoComposicaoLookup | null> {
        const crmNormalizado = this.normalizarValor(crm);
        const ufNormalizada = this.normalizarValor(uf);
        if (!crmNormalizado || !ufNormalizada) {
            return of(null);
        }

        let params = new HttpParams();
        params = params.set('crm', crmNormalizado);
        params = params.set('uf', ufNormalizada);
        params = params.set('tipo', 'MEDICO');
        params = params.set('page', '0');
        params = params.set('size', '30');

        return this.http.get<Page<PessoaLocalizadaApiResponse>>(this.pessoaLocalizarApiUrl, { params }).pipe(
            map((response) => {
                const registros = response?.content || [];
                if (!registros.length) {
                    return null;
                }

                const crmEntradaNormalizado = this.normalizarCrmComparacao(crmNormalizado);
                const ufEntradaNormalizada = this.normalizarValor(ufNormalizada);

                const candidato =
                    registros.find((registro) => this.normalizarValor(registro.uf || '') === ufEntradaNormalizada && this.normalizarCrmComparacao(registro.crm || '') === crmEntradaNormalizado) ||
                    registros.find((registro) => this.normalizarValor(registro.uf || '') === ufEntradaNormalizada) ||
                    registros[0];

                const idMedico = Number(candidato?.id);
                const ufMedico = this.normalizarValor(candidato?.uf || ufEntradaNormalizada);
                if (!Number.isFinite(idMedico) || idMedico <= 0 || !ufMedico) {
                    return null;
                }

                return {
                    idMedico,
                    ufMedico,
                    nome: (candidato?.nome || '').trim()
                };
            }),
            catchError(this.handleError)
        );
    }

    private mapearLinhas(
        response: Array<{
            id: number;
            uf: string;
            nome: string;
            cargo: string;
            dataEntrada: string | null;
            dataSaida: string | null;
        }>
    ): ComposicaoLinha[] {
        return response.map((item) => this.mapearLinha(item));
    }

    private mapearLinha(item: { id: number; uf: string; nome: string; cargo: string; dataEntrada: string | null; dataSaida: string | null }): ComposicaoLinha {
        return {
            idMedico: Number(item.id),
            ufMedico: item.uf || '-',
            uf: item.uf || '-',
            nome: item.nome || '-',
            cargo: item.cargo || '-',
            dataEntrada: this.formatarData(item.dataEntrada),
            dataSaida: this.formatarData(item.dataSaida),
            dataEntradaIso: item.dataEntrada,
            dataSaidaIso: item.dataSaida
        };
    }

    private formatarData(data: string | null): string {
        if (!data) {
            return '-';
        }

        const [ano, mes, dia] = data.split('-');
        if (!ano || !mes || !dia) {
            return data;
        }

        return `${dia}/${mes}/${ano}`;
    }

    private normalizarValor(valor: string): string {
        return (valor || '').toString().trim().toUpperCase();
    }

    private normalizarCrmComparacao(valor: string): string {
        return this.normalizarValor(valor).replace(/[^0-9A-Z]/g, '');
    }
}
